import { memo, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner'
import {
  useFetchFAQ,
  useUpdateFaQs,
} from '../../../../hooks/apiHooks/twinHooks'
import { useTwinData } from '../../../../hooks/storeHooks/useTwin'
import { Heading } from '../Heading'
import { CsvUpload } from './CsvUpload'
import { FaqItem } from './FaqItem'
import { Faq } from './FaqTab'

interface FaqManagementSectionProps {
  openNextTab: () => void
  isReadyStatus: boolean
  mentorId: string
}

const FaqManagementSection: React.FC<FaqManagementSectionProps> = memo(
  ({ openNextTab, mentorId, isReadyStatus }) => {
    const { faqs, addFaq, updateFaq, deleteFaq, setFaqs } = useTwinData()

    const { mutateAsync: updateFaQs, isPending: isUpdatingFaQs } =
      useUpdateFaQs()

    const { data: faqsData, isFetching: faqsFetching } = useFetchFAQ({
      mentorId,
    })

    const handleAddQuestion = () => {
      addFaq({
        question: '',
        answer: '',
        id: crypto.randomUUID(),
        isNew: true,
      })
    }

    useEffect(() => {
      if (faqsData) {
        setFaqs(faqsData.items)
      }
    }, [faqsData])

    const handleChangeItem = (faq: Faq) => {
      updateFaq(faq)
    }

    const handleDeleteItem = (id: string) => {
      deleteFaq(id)
    }

    const isValid = useMemo(() => {
      return faqs.every(faq => faq.question.trim() && faq.answer.trim())
    }, [faqs])

    const handleContinue = () => {
      if (!isValid || !faqs.length) return
      try {
        openNextTab()
      } catch (error) {
        console.error('Error setting FAQs:', error)
      }
    }

    const handleSave = async () => {
      if (!mentorId || !isValid) return
      try {
        const processedFaqs = faqs.map(faq => {
          if (faq.isNew) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { id, isNew, ...rest } = faq
            return rest
          }
          return faq
        })

        await updateFaQs({
          mentorId,
          faqs: processedFaqs,
        })

        setFaqs(processedFaqs)
        toast.success('FAQs saved successfully!')
      } catch {
        toast.error('Failed to save FAQs. Please try again.')
      }
    }

    const hasFaq = faqs.length

    return (
      <div className="max-w-full w-[600px] w- mb-auto sm:mb-0">
        <Heading
          title="FAQs"
          subtitle="Helps your digital twin provide better and more accurate responses"
        />

        <div className="h-full sm:h-[50vh] flex flex-col items-center justify-center mb-8">
          {faqsFetching ? (
            <div className="mt-4 flex  justify-center">
              <AnimatedSpinner size={24} />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-center gap-4 mb-8">
                <CsvUpload />
                <button className="btn btn-primary" onClick={handleAddQuestion}>
                  <i className="fa-solid fa-plus mr-2" />
                  Add question
                </button>
              </div>{' '}
              {hasFaq ? (
                <div className="flex-1 overflow-auto h-full w-full">
                  {faqs.map(faq => (
                    <FaqItem
                      key={faq.id}
                      faq={faq}
                      onChange={handleChangeItem}
                      onDelete={handleDeleteItem}
                    />
                  ))}
                </div>
              ) : (
                <>
                  <a
                    href="/faq_sample.csv"
                    download="faq_sample.csv"
                    className="underline "
                  >
                    Download sample CSV
                  </a>
                  <p className="mt-4 text-sm text-white/50 text-center">
                    No FAQs yet. Click "Add Question" or "Upload CSV" to get
                    started.
                  </p>
                </>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-center gap-4 mb-8">
          {isReadyStatus && mentorId ? (
            <>
              <Link
                data-testid="edit-faq-back-btn"
                to="/generate-twin"
                className="btn btn-primary sm:!grow-0 !grow"
              >
                <i className="fa-solid fa-arrow-left mr-2" />
                Back
              </Link>
              <button
                data-testid="edit-faq-save-btn"
                className="btn btn-primary sm:!grow-0 !grow"
                onClick={handleSave}
                disabled={isUpdatingFaQs || !isValid}
              >
                Update
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={openNextTab}>
                Do it later
              </button>
              <button
                className="btn btn-primary"
                onClick={handleContinue}
                disabled={!isValid || !hasFaq}
              >
                Continue
                <i className="fa-solid fa-arrow-right ml-2" />
              </button>
            </>
          )}
        </div>
      </div>
    )
  },
)

export { FaqManagementSection }
